<script>
  import { get, sync, call } from 'vuex-pathify'
  import { events } from '@/helpers/gtm'
  import SelectPlanCard from '@/components/subscribe/SelectPlanCard'
  import screen from '@/helpers/screen'
  import SubscribeWaitlistLandingPage from '@/components/subscribe/SubscribeWaitlistLandingPage'
  import BaseSpinner from '@/components/base/BaseSpinner'
  import SubscribeSelectPlanFooter from './SubscribeSelectPlanFooter.vue'

  export default {
    components: {
      SelectPlanCard,
      SubscribeWaitlistLandingPage,
      SubscribeSelectPlanFooter,
      BaseSpinner,
    },
    data() {
      return {
        loading: true,
      }
    },
    computed: {
      ...get('prismic', ['getSubscribeBoxAndPlansCards']),
      ...get('config', ['isCurrentBoxWaitlist']),
      ...get('feature', ['flags']),
      ...get('account', ['isAlltruePlusSubscriber']),
      ...sync('subscribe', ['selectedPlanHandle']),
      ...sync('subscribe', ['selectedBoxHandle']),
      screen,
      planItems() {
        const getData = this.getSubscribeBoxAndPlansCards(this.selectedBoxHandle)
        if (getData && Array.isArray(getData) && getData.length > 0) {
          const getPlansFromBox = getData[0]
          return getPlansFromBox.items || []
        } else {
          return []
        }
      },
      showAlltruePlus() {
        const isAllTruePlusEnabled = this.flags['alltrue-plus-option'] ?? false
        return isAllTruePlusEnabled && !this.isAlltruePlusSubscriber
      },
    },
    async mounted() {
      // Get boxHandle from URL parameters
      const boxHandle = this.$route.query.boxHandle

      // If boxHandle exists in URL, set it
      if (boxHandle) {
        this.selectedBoxHandle = boxHandle
      }

      if (!this.selectedBoxHandle) {
        this.$navigate({ name: 'SubscribeBox' })
      }
    },
    async created() {
      this.loading = true
      try {
        await this.loadSubscriptionBoxandPlan()
        events.log({ name: 'viewed plan selection' })
        events.startSubscriptionCheckoutFunnel({ email: this.username })
        if (!this.isAlltruePlusSubscriber) {
          this.observeFlag('alltrue-plus-option')
        }
      } finally {
        this.loading = false
      }
    },
    methods: {
      ...call('prismic', ['loadSubscriptionBoxandPlan']),
      ...call('feature', ['observeFlag']),
      selectPlan(planHandle) {
        this.selectedPlanHandle = planHandle
        events.log({
          name: 'selected plan',
          data: { plan: planHandle },
        })
        events.selectedSubscriptionPlan({
          email: this.username,
          planHandle,
        })
        this.$navigate({ name: 'SubscribeSignup', params: { subscribe: 'subscribe' } })
      },
    },
  }
</script>

<template>
  <div>
    <SubscribeWaitlistLandingPage
      v-if="isCurrentBoxWaitlist"
      @navigateToSubscribe="selectPlan('quarterly')"
    />
    <div v-else class="mb-12">
      <!-- Banner with header -->
      <div
        class="bg-black text-dawn py-6 px-6 sm:px-9 text-center text-sm leading-snug-tight mb-4 relative"
      >
        <div class="mx-auto max-w-320">
          <h1 class="font-semibold text-base md:text-lg leading-snug-tight">Select a Plan Type </h1>
          <p class="max-w-120 m-auto">
            Choose the curation that suits you best! Limited quantities available. Cancel anytime.
            Free shipping.*
          </p>
          <BaseIcon
            v-if="screen.lg"
            :size="5"
            label="back to membership selection page"
            class="absolute left-0 top-1/2 transform -translate-y-1/2"
            @click="$navigate({ name: 'SubscribeBox' })"
          >
            <IconArrowLeft />
          </BaseIcon>
        </div>
      </div>

      <div
        class="flex flex-col sm:flex-row justify-center px-3 sm:px-4 md:px-6 xl:px-0 mt-4 lg:mt-6 mb-6 gap-3"
      >
        <div v-for="plan in planItems" :key="plan.handle">
          <SelectPlanCard
            :key="plan.plan_code"
            :plantype="plan.plan_type"
            :planboxtitle="plan.plan_box_title"
            :plancode="plan.plan_code"
            :planprice="plan.price"
            :planpricetotal="plan.price_total"
            :plansingleboxprice="plan.single_box_price"
            :planpricebyline="plan.price_byline"
            :planbenefits="plan.benefits"
            :benefitsheader="plan.benefits_header"
            :mobilebenefitspreview="plan.mobile_benefits_preview"
            :planreviewat="plan.review_at"
            :type="plan.plan_type"
            orientation="horizontal"
            :most-popular="true"
            @selectPlan="selectPlan(plan.plan_code)"
          />
        </div>
      </div>
    </div>
    <SubscribeSelectPlanFooter />
    <BaseSpinner v-if="loading" overlay="absolute" overlay-color="dawn-lt3" class="z-10" />
  </div>
</template>

<style lang="postcss" scoped>
  .max-width-two-cards {
    @apply px-3;

    @screen sm {
      @apply px-0;

      max-width: 404px;
    }

    @screen md {
      max-width: 476px;
    }

    @screen lg {
      max-width: 640px;
    }

    @screen xl {
      max-width: 728px;
    }
  }

  .max-width-three-cards {
    @apply px-3;

    @screen sm {
      @apply px-0;

      max-width: 612px;
    }

    @screen md {
      max-width: 720px;
    }

    @screen lg {
      max-width: 990px;
    }

    @screen xl {
      max-width: 1104px;
    }
  }
</style>
